/** Where a sign-in prompt or button sits; the one thing we learn about why someone opened the dialog */
export type SignInSource = "header" | "home-banner" | "follow" | "save" | "player" | "account" | "feed-teaser";

export type SignInEvent = "signin_prompt_shown" | "signin_opened" | "code_requested" | "signed_in" | "signin_abandoned";

export type SendEvent = (event: SignInEvent, props: Record<string, string>) => void;

const sendToPlausible: SendEvent = (event, props) => {
    const plausible = (window as { plausible?: (name: string, options?: { props: Record<string, string> }) => void })
        .plausible;
    plausible?.(event, { props });
};

/**
 * Counts how far listeners get through sign-in, per source. Events carry only the source and a
 * stage: never the email address, the code or anything about the account.
 */
export class SignInFunnel {
    private source: SignInSource | undefined;
    private codeSent = false;
    private open = false;

    constructor(private readonly send: SendEvent = sendToPlausible) {}

    promptShown(source: SignInSource) {
        this.send("signin_prompt_shown", { source });
    }

    opened(source: SignInSource) {
        this.source = source;
        this.codeSent = false;
        this.open = true;
        this.send("signin_opened", { source });
    }

    /** `resend`: another code for the same address, not a first request */
    codeRequested(resend: boolean) {
        this.codeSent = true;
        this.send("code_requested", { ...this.sourceProps(), resend: String(resend) });
    }

    signedIn(newUser: boolean) {
        this.open = false;
        this.send("signed_in", { ...this.sourceProps(), new_user: String(newUser) });
    }

    /** The dialog was closed without signing in; `stage` says whether a code had been sent */
    closed() {
        if (!this.open) return;
        this.open = false;
        this.send("signin_abandoned", { ...this.sourceProps(), stage: this.codeSent ? "code" : "email" });
    }

    private sourceProps(): Record<string, string> {
        return this.source ? { source: this.source } : {};
    }
}

export const signInFunnel = new SignInFunnel();
